/**
 * Round a monetary value to 2 decimals (the precision DIAN works at).
 *
 * Nudges by `Number.EPSILON` before scaling to dodge the IEEE-754 midpoint
 * bug: a plain `Math.round(1.005 * 100) / 100` yields `1` because `1.005` is
 * stored as `1.00499…`. The epsilon push lands it on `1.01`. This matches the
 * existing rounding in `new-note.component.ts`. `value || 0` coerces
 * NaN/undefined to 0.
 */
export const round2 = (value: number): number => {
  return Math.round(((value || 0) + Number.EPSILON) * 100) / 100;
};

/**
 * Per-line rounding budget for credit-note totals. The note total is
 * recomputed at full float precision while the invoice total is stored
 * pre-rounded, so a legitimate full credit can overshoot by up to half a
 * cent per line. One cent per line (min one cent) absorbs that noise.
 *
 * Shared by the validation guard and the builder clamp so they can never
 * diverge: if the budget changes, both move together.
 */
export const roundingTolerance = (lineCount: number): number => {
  return Math.max(0.01, lineCount * 0.01);
};

/**
 * Remaining balance on a treasury/advance-payment record (total minus what's
 * already been applied elsewhere). Shared by the sales and purchase payment
 * modals — both filter/sort advance-payment options by this same value.
 */
export const calculateAvailable = (total: number, used: number): number => {
  return round2(total - used);
};

/**
 * True when a form value, once rounded to cents, equals a rounded target —
 * used to decide whether a payment amount exactly cancels out a target
 * balance (e.g. the invoice's missing-paid amount, or what's left after
 * advance-payment/credit-balance application), in which case the payment
 * modals classify the entry as a "Cancellation" instead of a plain "Credit".
 * Both `value` and `target` are rounded before comparing so float noise from
 * upstream sums doesn't produce a false negative.
 */
export const valueMatchesRoundedTarget = (
  value: string | number | null,
  target: number,
): boolean => {
  if (value === null || value === '' || target <= 0) {
    return false;
  }

  const numericValue = typeof value === 'number' ? value : parseFloat(value);

  return !isNaN(numericValue) && round2(numericValue) === round2(target);
};
